import React from 'react';
import type { CancellationPolicyTemplate } from '../../features/placeSlice';

interface Props {
  value?: string | null;
  catalog: CancellationPolicyTemplate[];
  onChange: (code: string) => void;
  disabled?: boolean;
  /** When set, a "Custom" card is added after the catalog; `customContent` shows under it while selected. */
  customSelected?: boolean;
  onSelectCustom?: () => void;
  customContent?: React.ReactNode;
}

/**
 * Airbnb-style radio-card list: each cancellation policy is shown as a selectable
 * card with its name AND its refund-rule description, so the host can see what each
 * policy means while choosing. The selected card is highlighted.
 */
export function CancellationPolicyPicker({
  value,
  catalog,
  onChange,
  disabled,
  customSelected,
  onSelectCustom,
  customContent,
}: Props) {
  if (catalog.length === 0 && !onSelectCustom) {
    return (
      <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>
        No cancellation policies available.
      </p>
    );
  }

  return (
    <div role="radiogroup" aria-label="Cancellation policy" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {catalog.map((c) => {
        const isSelected = !customSelected && c.code === value;
        return (
          <button
            key={c.code}
            type="button"
            role="radio"
            aria-checked={isSelected}
            disabled={disabled}
            onClick={() => onChange(c.code)}
            style={{
              textAlign: 'left',
              width: '100%',
              cursor: disabled ? 'not-allowed' : 'pointer',
              background: isSelected ? 'rgba(241, 102, 47, 0.06)' : '#ffffff',
              border: isSelected ? '2px solid #f1662f' : '1px solid #e5e7eb',
              borderRadius: 12,
              padding: isSelected ? '11px 13px' : '12px 14px',
              opacity: disabled ? 0.6 : 1,
              transition: 'border-color 0.15s, background 0.15s',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span
                aria-hidden
                style={{
                  width: 16,
                  height: 16,
                  flexShrink: 0,
                  borderRadius: '50%',
                  border: isSelected ? '5px solid #f1662f' : '2px solid #c4c4c4',
                  boxSizing: 'border-box',
                }}
              />
              <span style={{ fontSize: 15, fontWeight: 600, color: '#1a1a1a' }}>{c.displayName}</span>
            </div>
            {c.description && (
              <p style={{ margin: '6px 0 0 24px', fontSize: 13, lineHeight: 1.45, color: '#6b7280' }}>
                {c.description}
              </p>
            )}
          </button>
        );
      })}
      {onSelectCustom ? (
        <div
          role="radio"
          aria-checked={!!customSelected}
          tabIndex={0}
          onClick={disabled ? undefined : onSelectCustom}
          onKeyDown={(e) => {
            if (!disabled && (e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
              e.preventDefault();
              onSelectCustom();
            }
          }}
          style={{
            textAlign: 'left',
            width: '100%',
            boxSizing: 'border-box',
            cursor: disabled ? 'not-allowed' : 'pointer',
            background: customSelected ? 'rgba(241, 102, 47, 0.06)' : '#ffffff',
            border: customSelected ? '2px solid #f1662f' : '1px solid #e5e7eb',
            borderRadius: 12,
            padding: customSelected ? '11px 13px' : '12px 14px',
            opacity: disabled ? 0.6 : 1,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span
              aria-hidden
              style={{
                width: 16,
                height: 16,
                flexShrink: 0,
                borderRadius: '50%',
                border: customSelected ? '5px solid #f1662f' : '2px solid #c4c4c4',
                boxSizing: 'border-box',
              }}
            />
            <span style={{ fontSize: 15, fontWeight: 600, color: '#1a1a1a' }}>Custom</span>
          </div>
          <p style={{ margin: '6px 0 0 24px', fontSize: 13, lineHeight: 1.45, color: '#6b7280' }}>
            Set your own refund rules: how many days before check-in guests get a full or partial refund.
          </p>
          {customSelected ? customContent : null}
        </div>
      ) : null}
    </div>
  );
}
